// LiveStreamChatroomGiftItem.jsx
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import DecryptionWrapper from '../container/DecryptionWrapper.js';

import env from '../resource/env.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import { MediaAssetFormat } from '../resource/getMediaAsset.js';
import getAPNG from '../resource/getAPNG.js';

export const LiveStreamChatroomGiftItem = ({
  giftImage = '',
  isPagGift = false,
  isAnimationGift = false,
  shouldShowMainAsset = false,
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    async function playAnimationGift() {
      const { isAPNG, apng } = await getAPNG({ imageSource: giftImage });
      if (!isAPNG || !canvasRef.current) {
        return;
      }

      canvasRef.current.width = apng.width;
      canvasRef.current.height = apng.height;
      const player = await apng.getPlayer(canvasRef.current.getContext('2d'));
      player.play();
    }

    if (isAnimationGift && !shouldShowMainAsset) {
      playAnimationGift();
    }
  }, [giftImage, isAnimationGift, shouldShowMainAsset]);

  const renderGiftImage = () => {
    const isPublicImage = giftImage.includes(env.PUBLIC_URL_PREFIX);
    if (isPublicImage) {
      const [avifSrcSet, webpSrcSet, pngSrcSet] = [
        MediaAssetFormat.AVIF,
        MediaAssetFormat.WEBP,
        MediaAssetFormat.PNG,
      ].map(format => getPublicSrcSet({ href: giftImage, size: 128, format }));
      return (
        <GiftImageBlock>
          <GiftImage>
            <DecryptionWrapper resourceUrl={pngSrcSet.src}>
              <source srcSet={avifSrcSet.srcSet} type="image/avif" />
              <source srcSet={webpSrcSet.srcSet} type="image/webp" />
              <img {...pngSrcSet} />
            </DecryptionWrapper>
          </GiftImage>
        </GiftImageBlock>
      );
    }

    return (
      <GiftImageBlock>
        <GiftImage>
          <DecryptionWrapper resourceUrl={giftImage}>
            <img src={giftImage} />
          </DecryptionWrapper>
        </GiftImage>
      </GiftImageBlock>
    );
  };

  const renderAnimationGift = () => {
    return (
      <GiftImageBlock>
        <GiftImage>
          <GiftCanvas ref={canvasRef} />
        </GiftImage>
      </GiftImageBlock>
    );
  };

  return (
    <StyledLiveStreamChatroomGiftItem>
      {(() => {
        if (isPagGift && shouldShowMainAsset) {
          return null;
        }

        if (isAnimationGift) {
          return shouldShowMainAsset ? null : renderAnimationGift();
        }

        return renderGiftImage();
      })()}
    </StyledLiveStreamChatroomGiftItem>
  );
};

LiveStreamChatroomGiftItem.propTypes = {
  giftImage: PropTypes.string,
  isPagGift: PropTypes.bool,
  isAnimationGift: PropTypes.bool,
  shouldShowMainAsset: PropTypes.bool,
};

const StyledLiveStreamChatroomGiftItem = styled.div`
  min-width: 20px;
`;

const GiftImageBlock = styled.div`
  position: relative;
  margin-left: 4px;
  width: 68px;
`;

const GiftImage = styled.picture`
  position: absolute;
  right: 8px;
  height: 64px;
  width: 64px;
  transform: translateY(-50%);

  & > img {
    width: 100%;
    height: 100%;
  }
`;

const GiftCanvas = styled.canvas`
  object-fit: contain;
  width: 64px;
  height: 64px;
`;

export default LiveStreamChatroomGiftItem;
